import ThemedSelect from "../shared/ThemedSelect.jsx";
import { useEffect, useMemo, useState } from "react";
import RulesHelp from "../shared/RulesHelp.jsx";
import { Button, Form, Table } from "react-bootstrap";
import { formatDate } from "../../utils/date.js";
import { getCurrentUser } from "../../services/apiClient.js";
import StarRating, { Stars } from "./StarRating.jsx";
import CompanyDocumentsPanel from "./CompanyDocumentsPanel.jsx";
import {
  createEntityRow,
  listEntityRows,
  setEntityRowActive,
  updateEntityRow,
  listEntityChildRows,
  createEntityChildRow,
  updateEntityChildRow,
  deleteEntityChildRow,
} from "../../services/apiClient.js";

// Generic list + add/edit for any System Data View entity. Everything it renders
// comes from the entity config (config/dataEntities.js), so there is one of these
// for every lookup table rather than a bespoke page each. The "show inactive"
// filter is owned by the parent (SystemDataPage) and passed in.

// A field may name a `defaultValue` for NEW rows (a checkbox that should start
// ticked). Without it every field starts blank, and the write path coerces a
// blank checkbox to false — so a column whose database default is true was
// silently saved false by every row added on screen (found 2026-09-05 on the
// Order Fee Profiles' "Markup as its own line").
function buildEmptyForm(entity) {
  const form = {};
  for (const field of entity.fields) form[field.name] = field.defaultValue ?? "";
  return form;
}

// Child rows of the row being edited (e.g. a company's contacts — legacy
// CDCompanyDetails contacts grid). Inline row editing: Edit turns the row's
// cells into inputs; New adds a blank editor row. Saves are immediate (per
// row), independent of the parent form's Save. Hard delete, as 1.0.
function ChildRowsManager({ entityKey, parentId, child, canWrite }) {
  const [rows, setRows] = useState([]);
  const [editingId, setEditingId] = useState(null); // row id, or "new"
  const [form, setForm] = useState({});
  const [refOptions, setRefOptions] = useState({}); // refEntity -> rows
  const [error, setError] = useState("");

  useEffect(() => {
    listEntityChildRows(entityKey, parentId, child.key)
      .then(setRows)
      .catch(() => setError(`Unable to load ${child.label.toLowerCase()}.`));
    // Dropdown options for any reference fields (e.g. work centres).
    Promise.all(
      child.fields
        .filter((f) => f.type === "reference")
        .map((f) => listEntityRows(f.refEntity).then((data) => [f.refEntity, data]).catch(() => [f.refEntity, []]))
    ).then((pairs) => setRefOptions(Object.fromEntries(pairs)));
  }, [entityKey, parentId, child]);

  function refLabel(field, value) {
    if (!value) return "-";
    const option = (refOptions[field.refEntity] || []).find((r) => String(r.id) === String(value));
    return option ? option.name : `#${value}`;
  }

  function startEdit(row) {
    setError("");
    setEditingId(row ? row.id : "new");
    const next = {};
    for (const field of child.fields) {
      next[field.name] =
        row == null || row[field.name] == null
          ? field.type === "checkbox"
            ? false
            : ""
          : row[field.name];
    }
    setForm(next);
  }

  async function save() {
    try {
      if (editingId === "new") {
        const created = await createEntityChildRow(entityKey, parentId, child.key, form);
        setRows((current) => [...current, created]);
      } else {
        const updated = await updateEntityChildRow(entityKey, parentId, child.key, editingId, form);
        setRows((current) => current.map((r) => (r.id === updated.id ? updated : r)));
      }
      setEditingId(null);
    } catch (err) {
      setError(err.response?.data?.error || "Unable to save.");
    }
  }

  async function remove(row) {
    const label = row.name || cell(child.fields[0], row);
    if (!window.confirm(`Delete ${child.singular.toLowerCase()} "${label}"?`)) return;
    try {
      await deleteEntityChildRow(entityKey, parentId, child.key, row.id);
      setRows((current) => current.filter((r) => r.id !== row.id));
    } catch (err) {
      setError(err.response?.data?.error || "Unable to delete.");
    }
  }

  const saveDisabled = child.fields.some(
    (field) => field.required && !String(form[field.name] || "").trim()
  );

  function cell(field, row) {
    if (field.type === "checkbox") return row[field.name] ? "Yes" : "-";
    // nullLabel / zeroLabel: a blank or a 0 that MEANS something ("standard",
    // "off" on a fee percent) says so instead of a dash or a number nobody
    // should read as a rate.
    if (field.nullLabel != null && (row[field.name] == null || row[field.name] === "")) {
      return <span className="text-muted fst-italic" data-hint={field.nullHint || undefined}>{field.nullLabel}</span>;
    }
    if (field.type === "reference") return refLabel(field, row[field.name]);
    if (field.zeroLabel != null && row[field.name] != null && Number(row[field.name]) === 0) {
      return field.zeroLabel;
    }
    return row[field.name] == null || row[field.name] === "" ? "-" : row[field.name];
  }

  function input(field) {
    if (field.type === "checkbox") {
      return (
        <Form.Check
          type="switch"
          id={`${child.key}-${field.name}`}
          checked={form[field.name] === true || form[field.name] === "true"}
          onChange={(e) => setForm((c) => ({ ...c, [field.name]: e.target.checked }))}
        />
      );
    }
    if (field.type === "reference") {
      return (
        <ThemedSelect
          size="sm"
          value={form[field.name] || ""}
          onChange={(e) => setForm((c) => ({ ...c, [field.name]: e.target.value }))}
        >
          <option value="">— Choose —</option>
          {(refOptions[field.refEntity] || []).map((o) => (
            <option key={o.id} value={o.id}>{o.name}</option>
          ))}
        </ThemedSelect>
      );
    }
    return (
      <Form.Control
        size="sm"
        type={field.type === "email" ? "email" : "text"}
        value={form[field.name]}
        onChange={(e) => setForm((c) => ({ ...c, [field.name]: e.target.value }))}
      />
    );
  }

  const editorRow = (key) => (
    <tr key={key}>
      {child.fields.map((field) => (
        <td key={field.name}>{input(field)}</td>
      ))}
      <td className="text-end text-nowrap">
        <Button size="sm" className="me-2" onClick={save} disabled={saveDisabled}>
          Save
        </Button>
        <Button size="sm" variant="secondary" onClick={() => setEditingId(null)}>
          Cancel
        </Button>
      </td>
    </tr>
  );

  return (
    <div className="mt-4">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6 className="text-uppercase fw-semibold small mb-0" style={{ letterSpacing: "0.05em" }}>
          {child.label}
        </h6>
        {canWrite && editingId == null ? (
          <Button size="sm" variant="outline-primary" onClick={() => startEdit(null)}>
            New {child.singular}
          </Button>
        ) : null}
      </div>
      {error ? <div className="alert alert-danger py-1 px-2 small">{error}</div> : null}
      <Table striped bordered hover responsive size="sm" className="mb-0 align-middle">
        <thead className="table-surface">
          <tr>
            {child.fields.map((field) => (
              <th key={field.name}>{field.label}</th>
            ))}
            <th className="text-end" style={{ width: 150 }}>
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) =>
            editingId === row.id ? (
              editorRow(row.id)
            ) : (
              <tr key={row.id}>
                {child.fields.map((field) => (
                  <td key={field.name}>{cell(field, row)}</td>
                ))}
                <td className="text-end text-nowrap">
                  {canWrite ? (
                    <>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="me-2"
                        onClick={() => startEdit(row)}
                      >
                        Edit
                      </Button>
                      <Button variant="outline-danger" size="sm" onClick={() => remove(row)}>
                        Delete
                      </Button>
                    </>
                  ) : (
                    <span className="text-muted small">—</span>
                  )}
                </td>
              </tr>
            )
          )}
          {editingId === "new" ? editorRow("new") : null}
          {rows.length === 0 && editingId !== "new" ? (
            <tr>
              <td colSpan={child.fields.length + 1} className="text-center text-muted">
                No {child.label.toLowerCase()}.
              </td>
            </tr>
          ) : null}
        </tbody>
      </Table>
    </div>
  );
}

// onDeepLinkDone: when the editor was opened via a deep link (?edit=<id>),
// Save/Cancel call this instead of returning to the list — the caller pops
// back to wherever the user came from (e.g. the H&S PL Expiry Alert).
function EntityManager({ entity, includeInactive = false, initialEditId = null, onDeepLinkDone = null }) {
  const [me, setMe] = useState(null);
  useEffect(() => {
    getCurrentUser().then(setMe).catch(() => {});
  }, []);
  // Structural lookups are managed by the system owner (server-enforced too).
  // KR 2026-08-28: System Data is VIEW-ONLY below admin — everyone can read
  // the lookups, admins edit them, and the structural (ownerOnly) entities
  // stay the system owner's alone. The server enforces the same
  // (routes/systemData.js: reads requireUser, writes requireAdmin + owner
  // check); this decides what RENDERS, so a viewer sees data, not dead
  // buttons that could only 403.
  const canWrite = entity.ownerOnly ? !!me?.is_system_owner : !!me?.is_admin;
  const [rows, setRows] = useState([]);
  const [refOptions, setRefOptions] = useState({});
  const [mode, setMode] = useState("list");
  const [editing, setEditing] = useState(null);
  const [deepLink, setDeepLink] = useState(false); // this edit came from a deep link
  const [form, setForm] = useState(() => buildEmptyForm(entity));
  const [error, setError] = useState("");
  // Fee-percent fields whose quick-pick is parked on "custom %" even while
  // the number box is still empty — without this the derived mode would snap
  // back to "standard" the moment custom was chosen. Reset with the form.
  const [forcedCustom, setForcedCustom] = useState(() => new Set());

  const listFields = useMemo(
    () => entity.fields.filter((field) => field.inList),
    [entity]
  );
  const referenceFields = useMemo(
    () => entity.fields.filter((field) => field.type === "reference"),
    [entity]
  );

  async function refresh() {
    const data = await listEntityRows(entity.key, { includeInactive });
    setRows(data);
    return data;
  }

  // Reload whenever the selected entity or the active filter changes. A
  // deep-linked row (?edit=<id>) opens its editor straight away.
  useEffect(() => {
    setMode("list");
    setEditing(null);
    setForm(buildEmptyForm(entity));
    setForcedCustom(new Set());
    setError("");
    refresh()
      .then((data) => {
        const row = initialEditId && data.find((r) => Number(r.id) === initialEditId);
        if (row) {
          startEdit(row);
          setDeepLink(true); // Save/Cancel pop back to the referring screen
        }
      })
      .catch((err) => setError(err.response?.data?.error || "Unable to load records."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity, includeInactive]);

  // Load dropdown options for any reference fields.
  useEffect(() => {
    let cancelled = false;
    Promise.all(
      referenceFields.map((field) =>
        listEntityRows(field.refEntity).then((data) => [field.refEntity, data])
      )
    )
      .then((pairs) => {
        if (!cancelled) setRefOptions(Object.fromEntries(pairs));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [referenceFields]);

  function refLabel(field, value) {
    if (!value) return "-";
    const option = (refOptions[field.refEntity] || []).find(
      (row) => String(row.id) === String(value)
    );
    return option ? option.name : `#${value}`;
  }

  function handleChange(event) {
    const { name, value, type, checked } = event.target;
    setForm((current) => ({ ...current, [name]: type === "checkbox" ? checked : value }));
  }

  function startAdd() {
    setError("");
    setEditing(null);
    setDeepLink(false); // user-initiated — normal list flow from here
    setForm(buildEmptyForm(entity));
    setForcedCustom(new Set());
    setMode("add");
  }

  function startEdit(row) {
    setError("");
    setDeepLink(false); // reset; the deep-link effect re-flags its own open
    setEditing(row);
    const next = {};
    for (const field of entity.fields) {
      // A multiselect's value is an array and must stay one: String() turned
      // ["a","b"] into "a,b" and every switch on Edit showed off (Robert
      // 2026-09-11, the first reason edited on the Owner Data page).
      next[field.name] =
        row[field.name] == null ? ""
        : field.type === "multiselect" ? (Array.isArray(row[field.name]) ? row[field.name] : [])
        : String(row[field.name]);
    }
    setForm(next);
    setForcedCustom(new Set());
    setMode("edit");
  }

  async function save() {
    try {
      if (mode === "add") {
        const created = await createEntityRow(entity.key, form);
        setRows((current) => [...current, created]);
      } else if (editing) {
        const updated = await updateEntityRow(entity.key, editing.id, form);
        setRows((current) =>
          current.map((row) => (row.id === updated.id ? updated : row))
        );
      }
      if (deepLink && onDeepLinkDone) {
        onDeepLinkDone(); // back to the screen that sent us (e.g. H&S alert)
        return;
      }
      setMode("list");
      setEditing(null);
    } catch (err) {
      setError(err.response?.data?.error || "Unable to save record.");
    }
  }

  async function toggleActive(row) {
    try {
      const updated = await setEntityRowActive(entity.key, row.id, !row.is_active);
      setRows((current) =>
        includeInactive
          ? current.map((r) => (r.id === updated.id ? updated : r))
          : current.filter((r) => r.id !== updated.id)
      );
    } catch (err) {
      setError(err.response?.data?.error || "Unable to update record.");
    }
  }

  const isForm = mode === "add" || mode === "edit";
  const saveDisabled = entity.fields.some(
    (field) => field.required && !String(form[field.name] || "").trim()
  );

  function renderCell(field, row) {
    if (field.type === "checkbox") return row[field.name] ? "Yes" : "-";
    if (field.type === "rating") return <Stars value={row[field.name]} />;
    if (field.type === "multiselect") {
      const chosen = Array.isArray(row[field.name]) ? row[field.name] : [];
      if (chosen.length === 0) return <span className="text-muted fst-italic">{field.emptyLabel || "any"}</span>;
      return (field.options || []).filter((o) => chosen.includes(o.value)).map((o) => o.label).join(", ");
    }
    // nullLabel / zeroLabel: a blank or a 0 that MEANS something ("standard",
    // "off" on a fee percent) says so instead of a dash or a number nobody
    // should read as a rate.
    if (field.nullLabel != null && (row[field.name] == null || row[field.name] === "")) {
      return <span className="text-muted fst-italic" data-hint={field.nullHint || undefined}>{field.nullLabel}</span>;
    }
    if (field.type === "reference") return refLabel(field, row[field.name]);
    if (field.zeroLabel != null && row[field.name] != null && Number(row[field.name]) === 0) {
      return field.zeroLabel;
    }
    if (row[field.name] == null || row[field.name] === "") return "-";
    return row[field.name];
  }

  function renderField(field) {
    if (field.type === "multiselect") {
      // One switch per option; nothing ticked = no restriction (crudService
      // stores NULL), which the list shows as the field's emptyLabel.
      const chosen = Array.isArray(form[field.name]) ? form[field.name] : [];
      const toggle = (value, on) =>
        setForm((current) => ({
          ...current,
          [field.name]: on ? [...chosen.filter((v) => v !== value), value] : chosen.filter((v) => v !== value),
        }));
      return (
        <div>
          {(field.options || []).map((o) => (
            <Form.Check
              key={o.value} type="switch" id={`${entity.key}-${field.name}-${o.value}`} label={o.label}
              checked={chosen.includes(o.value)} onChange={(e) => toggle(o.value, e.target.checked)}
            />
          ))}
          {field.help ? <div className="form-text">{field.help}</div> : null}
        </div>
      );
    }
    if (field.type === "rating") {
      return (
        <div>
          <StarRating
            value={form[field.name]}
            onChange={(v) => setForm((current) => ({ ...current, [field.name]: v }))}
          />
        </div>
      );
    }
    if (field.type === "reference") {
      const options = refOptions[field.refEntity] || [];
      return (
        <ThemedSelect name={field.name} value={form[field.name]} onChange={handleChange}>
          <option value="">— None —</option>
          {options.map((option) => (
            <option key={option.id} value={option.id}>
              {option.name}
            </option>
          ))}
        </ThemedSelect>
      );
    }
    if (field.type === "checkbox") {
      return (
        <Form.Check
          type="switch"
          id={`field-${field.name}`}
          name={field.name}
          checked={form[field.name] === true || form[field.name] === "true"}
          onChange={handleChange}
        />
      );
    }
    if (field.type === "number") {
      // Say what the typed value will MEAN before it is saved (Robert
      // 2026-08-19): 0 on a fee percent is "off", blank is "standard" — the
      // same words the list shows, so nobody saves a 0 thinking it's a rate.
      const raw = form[field.name];
      const s = raw == null ? "" : String(raw).trim();
      const meaning =
        field.nullLabel != null && s === ""
          ? `${field.nullLabel}${field.nullHint ? ` — ${field.nullHint}` : ""}`
          : field.zeroLabel != null && s !== "" && Number(s) === 0
            ? `= ${field.zeroLabel} for these jobs`
            : null;
      // A field with BOTH special values (the fee percents) edits as a QUICK
      // PICK (Robert 2026-08-20): a bare number box hid the semantics —
      // "standard" was clearing the field, "off" was typing 0. The closed
      // dropdown always reads like the list does: off, standard, or "17.8%";
      // picking "custom %" opens the number box beside it.
      if (field.zeroLabel != null && field.nullLabel != null) {
        const mode = forcedCustom.has(field.name)
          ? "custom"
          : s === ""
            ? "standard"
            : Number(s) === 0
              ? "off"
              : "custom";
        const pick = (m) => {
          setForcedCustom((current) => {
            const next = new Set(current);
            if (m === "custom") next.add(field.name);
            else next.delete(field.name);
            return next;
          });
          if (m === "off") setForm((current) => ({ ...current, [field.name]: "0" }));
          if (m === "standard") setForm((current) => ({ ...current, [field.name]: "" }));
        };
        return (
          <>
            <div className="d-flex gap-2">
              <ThemedSelect
                value={mode}
                onChange={(e) => pick(e.target.value)}
                style={{ maxWidth: 170 }}
                data-hint={field.nullHint || undefined}
              >
                <option value="off">{field.zeroLabel}</option>
                <option value="standard">{field.nullLabel}</option>
                <option value="custom">
                  {mode === "custom" && s !== "" && Number(s) !== 0 ? `${Number(s)}%` : "custom %…"}
                </option>
              </ThemedSelect>
              {mode === "custom" ? (
                <Form.Control
                  type="number"
                  name={field.name}
                  value={form[field.name]}
                  onChange={handleChange}
                  placeholder="%"
                  style={{ maxWidth: 110 }}
                />
              ) : null}
            </div>
            {mode === "custom" && s === "" ? (
              <div className="form-text fst-italic">
                Enter the percent — left blank it saves as {field.nullLabel}.
              </div>
            ) : meaning ? (
              <div className="form-text fst-italic">{meaning}</div>
            ) : null}
          </>
        );
      }
      return (
        <>
          <Form.Control
            type="number"
            name={field.name}
            value={form[field.name]}
            onChange={handleChange}
          />
          {meaning ? (
            <div className="form-text fst-italic">{meaning}</div>
          ) : null}
        </>
      );
    }
    if (field.type === "textarea") {
      return (
        <Form.Control
          as="textarea"
          rows={2}
          name={field.name}
          value={form[field.name]}
          onChange={handleChange}
        />
      );
    }
    return (
      <Form.Control
        type={field.type === "email" ? "email" : "text"}
        name={field.name}
        value={form[field.name]}
        onChange={handleChange}
      />
    );
  }

  return (
    <>
      {error ? <div className="alert alert-danger">{error}</div> : null}

      {!isForm ? (
        <>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="mb-0">
              {entity.label}
              {entity.helpTopic ? <RulesHelp topic={entity.helpTopic} /> : null}
              {/* Business-day rules depend on this list's horizon — show how
                  far it reaches (pairs with the system notice when it's low). */}
              {entity.key === "public-holidays" && rows.length > 0 ? (
                <span className="small text-muted fw-normal ms-2">
                  — supported up to {formatDate(rows.map((r) => r.holiday_date).sort().at(-1))}
                </span>
              ) : null}
            </h5>
            {canWrite ? (
              <Button size="sm" onClick={startAdd}>
                New {entity.singular}
              </Button>
            ) : (
              <span className="small text-muted">
                {entity.ownerOnly ? "Managed by the system owner" : "View only — admins make changes"}
              </span>
            )}
          </div>
          <Table striped bordered hover responsive size="sm" className="mb-0 align-middle">
            <thead className="table-surface">
              <tr>
                {listFields.map((field) => (
                  <th key={field.name}>{field.label}</th>
                ))}
                {entity.hasActive ? <th>Active</th> : null}
                <th className="text-end" style={{ width: 170 }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  {listFields.map((field) => (
                    <td key={field.name}>{renderCell(field, row)}</td>
                  ))}
                  {entity.hasActive ? (
                    <td>{row.is_active ? "Yes" : "No"}</td>
                  ) : null}
                  <td className="text-end">
                    {canWrite ? (
                      <>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="me-2"
                          onClick={() => startEdit(row)}
                        >
                          Edit
                        </Button>
                        {entity.hasActive ? (
                          <Button
                            variant={row.is_active ? "danger" : "success"}
                            size="sm"
                            onClick={() => toggleActive(row)}
                          >
                            {row.is_active ? "Set Inactive" : "Set Active"}
                          </Button>
                        ) : null}
                      </>
                    ) : (
                      <span className="text-muted small">—</span>
                    )}
                  </td>
                </tr>
              ))}
              {rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={listFields.length + (entity.hasActive ? 2 : 1)}
                    className="text-center text-muted"
                  >
                    No records.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </Table>
        </>
      ) : (
        <div className="border rounded p-3">
          <h6 className="mb-3">
            {mode === "add" ? `Add ${entity.singular}` : `Edit ${entity.singular}`}
          </h6>
          <Form>
            {entity.fields.map((field) => (
              <Form.Group className="mb-3" key={field.name}>
                <Form.Label>
                  {field.label}
                  {field.required ? " *" : ""}
                </Form.Label>
                {renderField(field)}
              </Form.Group>
            ))}
            <Button className="me-2" onClick={save} disabled={saveDisabled}>
              Save
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                if (deepLink && onDeepLinkDone) onDeepLinkDone();
                else setMode("list");
                setDeepLink(false);
              }}
            >
              Cancel
            </Button>
          </Form>
          {(entity.children || []).map((child) =>
            mode === "edit" && editing ? (
              <ChildRowsManager
                key={child.key}
                entityKey={entity.key}
                parentId={editing.id}
                child={child}
                canWrite={canWrite}
              />
            ) : (
              <p key={child.key} className="text-muted small mt-3 mb-0">
                Save the {entity.singular.toLowerCase()} first to add {child.label.toLowerCase()}.
              </p>
            )
          )}
          {entity.documents && mode === "edit" && editing ? (
            <CompanyDocumentsPanel companyId={editing.id} canWrite={canWrite} />
          ) : null}
        </div>
      )}
    </>
  );
}

export default EntityManager;
